import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getOverallStandings, getGroupPreview } from '../../services/standingsApiService';
import championshipService from '../../services/championshipService';
import useAuth from '../../hooks/useAuth';
import './Standings.css';

// Helper function para verificar grupos
const hasGroups = (format) => {
  if (!format) return false;
  const lowerCaseFormat = format.toLowerCase();
  return lowerCaseFormat.includes('fase de grupos') || lowerCaseFormat.includes('formato personalizado');
};

// Helper function para montar URL da imagem (apenas para o logo do campeonato no print)
const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Helper: Calcula o aproveitamento (%) com base em jogos, vitórias e empates
const calculateAproveitamento = (item) => {
  const matches = Number(item?.matchesPlayed) || 0;
  if (matches === 0) return 0;
  const maxPoints = matches * 3;
  const earnedPoints = (Number(item?.wins) || 0) * 3 + (Number(item?.draws) || 0);
  return Math.round((earnedPoints / maxPoints) * 100);
};

// ANIMAÇÃO: Loader
const StandingsLoader = () => (
  <div className="loader-container">
    <div className="soccer-ball">⚽</div>
    <p>Carregando classificação...</p>
  </div>
);

// --- Componente Modal para Critérios ---
const CriteriaModal = ({ isOpen, onClose, currentCriteria, onSave }) => {
  const [localCriteria, setLocalCriteria] = useState(currentCriteria);

  useEffect(() => {
    if (isOpen) {
      const ALL_CRITERIA = ['points', 'wins', 'goalDifference', 'goalsFor', 'goalsAgainst', 'redCards', 'yellowCards', 'headToHead'];
      const merged = [
        ...currentCriteria,
        ...ALL_CRITERIA.filter(c => !currentCriteria.includes(c)),
      ];
      setLocalCriteria(merged);
    }
  }, [isOpen, currentCriteria]);

  if (!isOpen) return null;

  const moveItem = (index, direction) => {
    const newCriteria = [...localCriteria];
    const item = newCriteria[index];
    newCriteria.splice(index, 1);
    newCriteria.splice(index + direction, 0, item);
    setLocalCriteria(newCriteria);
  };

  const labels = {
    points: "Pontos",
    wins: "Vitórias",
    goalDifference: "Saldo de Gols",
    goalsFor: "Gols Pró",
    goalsAgainst: "Gols Sofridos (Menos é melhor)",
    headToHead: "Confronto Direto",
    yellowCards: "Menos Cartões Amarelos",
    redCards: "Menos Cartões Vermelhos",
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content criteria-modal">
        <h3>Critérios de Desempate</h3>
        <p style={{ fontSize: '0.9rem', color: '#ccc', marginBottom: '15px' }}>
          Arraste a prioridade (o primeiro da lista é o mais importante).
        </p>
        
        <ul className="criteria-list">
          {localCriteria.map((crit, index) => (
            <li key={crit} className="criteria-item">
              <span>{index + 1}. {labels[crit] || crit}</span>
              <div className="criteria-actions">
                <button 
                  disabled={index === 0} 
                  onClick={() => moveItem(index, -1)}
                  className="arrow-btn"
                >⬆️</button>
                <button 
                  disabled={index === localCriteria.length - 1} 
                  onClick={() => moveItem(index, 1)}
                  className="arrow-btn"
                >⬇️</button>
              </div>
            </li>
          ))}
        </ul>

        <div className="modal-actions">
          <button onClick={onClose} className="cancel-btn">Cancelar</button>
          <button onClick={() => onSave(localCriteria)} className="save-btn">Salvar Ordem</button>
        </div>
      </div>
    </div>
  );
};

const Standings = () => {
  const { id } = useParams();
  const { user, loadingAuth } = useAuth();
  const [standings, setStandings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [championship, setChampionship] = useState(null);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  
  const canManageGroups = useMemo(() => {
    return !loadingAuth && user && user.role && ['admin', 'gestor'].includes(user.role.toLowerCase());
  }, [user, loadingAuth]);

  const mergeStandingsWithGroupData = useCallback((standingsData, championshipGroups) => {
    const merged = {};
    
    if (!Array.isArray(championshipGroups)) {
      console.warn('mergeStandingsWithGroupData received non-array for championshipGroups:', championshipGroups);
      return merged;
    }

    championshipGroups.forEach(group => {
      const groupName = group.name;
      const backendStandings = standingsData[groupName] || [];
      const rankedTeamIds = new Set(backendStandings.map(s => s.team._id));

      const sortedTeams = backendStandings.map(item => ({
        ...item,
        team: { 
          _id: item.team._id, 
          name: item.team.name
        }
      }));

      const zeroStatsTeams = [];
      if (group.teams && Array.isArray(group.teams)) {
        group.teams.forEach(teamInGroup => {
          if (!rankedTeamIds.has(teamInGroup._id)) {
            zeroStatsTeams.push({
              team: { 
                _id: teamInGroup._id, 
                name: teamInGroup.name
              },
              points: 0, matchesPlayed: 0, wins: 0, draws: 0, losses: 0,
              goalsFor: 0, goalsAgainst: 0, goalDifference: 0,
            });
          }
        });
      }

      zeroStatsTeams.sort((a, b) => a.team.name.localeCompare(b.team.name));
      merged[groupName] = [...sortedTeams, ...zeroStatsTeams];
    });

    return merged;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [championshipResponse, standingsResult, groupPreviewResponse] = await Promise.all([
        championshipService.getById(id),
        getOverallStandings(id),
        getGroupPreview(id)
      ]);

      const currentChampionship = championshipResponse.data || championshipResponse;
      
      if (!currentChampionship || typeof currentChampionship !== 'object' || Object.keys(currentChampionship).length === 0) {
        setError('Dados do campeonato inválidos ou não encontrados.');
        setLoading(false);
        return; 
      }
      
      setChampionship(currentChampionship);

      const definitiveGroups = (currentChampionship.groups && currentChampionship.groups.length > 0)
          ? currentChampionship.groups
          : (groupPreviewResponse.success && groupPreviewResponse.data ? groupPreviewResponse.data : []);

      const actualStandingsData = (standingsResult.success && standingsResult.data) 
          ? standingsResult.data 
          : {};

      const mergedStandings = mergeStandingsWithGroupData(actualStandingsData, definitiveGroups);
      setStandings(mergedStandings);

    } catch (err) {
      console.error('Falha ao carregar dados para classificação:', err);
      setError(`Falha ao carregar dados: ${err.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  }, [id, mergeStandingsWithGroupData]);

  useEffect(() => {
    if (!loadingAuth) {
      fetchData();
    }
  }, [fetchData, loadingAuth]);

  const handlePrint = () => {
    let printContent = '';
    const champName = championship ? championship.name.toUpperCase() : 'CAMPEONATO';
    
    const logoUrl = championship && championship.logo ? getImageUrl(championship.logo) : null;
    let logoHtml = '';
    
    if (logoUrl) {
      logoHtml = `
        <div class="sumula-top-logo-container">
          <img src="${logoUrl}" class="sumula-top-logo" alt="Logo do Campeonato" />
        </div>
      `;
    } else {
      logoHtml = `<div style="text-align:center; margin-bottom:20px;"><h2>${champName}</h2></div>`;
    }

    Object.entries(standings).forEach(([groupName, groupTeams]) => {
      printContent += `
        <div class="group-wrapper">
          <table class="print-standings-table">
            <thead>
              <tr class="main-title-row">
                <th colspan="13">${champName} - GRUPO ${groupName.toUpperCase()}</th>
              </tr>
              <tr class="header-row">
                <th>Classif.</th>
                <th class="equipes-header">Equipes</th>
                <th>PTS</th>
                <th>JG</th>
                <th>V</th>
                <th>D</th>
                <th>E</th>
                <th>GM</th>
                <th>GS</th>
                <th>SG</th>
                <th>CA</th>
                <th>CV</th>
                <th>AP%</th>
              </tr>
            </thead>
            <tbody>
              ${groupTeams.map((item, index) => `
                <tr>
                  <td class="classif-cell">${index + 1}º</td>
                  <td class="equipes-cell">${item.team.name.toUpperCase()}</td>
                  <td class="data-cell">${item.points}</td>
                  <td class="data-cell">${item.matchesPlayed}</td>
                  <td class="data-cell">${item.wins}</td>
                  <td class="data-cell">${item.losses}</td>
                  <td class="data-cell">${item.draws}</td>
                  <td class="data-cell">${item.goalsFor}</td>
                  <td class="data-cell">${item.goalsAgainst}</td>
                  <td class="data-cell">${item.goalDifference}</td>
                  <td class="data-cell">${item.yellowCards || 0}</td>
                  <td class="data-cell">${item.redCards || 0}</td>
                  <td class="data-cell aproveitamento-cell">${calculateAproveitamento(item)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    });
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Classificação - ${champName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .sumula-top-logo-container {
              text-align: center;
              margin-bottom: 20px;
            }
            .sumula-top-logo {
              max-height: 100px;
              max-width: 100%;
              object-fit: contain;
            }
            .group-wrapper { page-break-inside: avoid; margin-bottom: 25px; }
            .print-standings-table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
            }
            .print-standings-table th, .print-standings-table td {
              border: 1px solid #000;
              padding: 5px;
              font-size: 11pt;
            }
            .main-title-row th, .header-row th {
              background-color: #E0E0E0;
              font-weight: bold;
              text-align: center;
            }
            .main-title-row th { font-size: 14pt; }
            .equipes-header { width: 38%; text-align: left !important; padding-left: 10px !important; }
            .classif-cell, .data-cell { text-align: center; }
            .equipes-cell { text-align: left; padding-left: 10px; }
            .aproveitamento-cell { font-weight: bold; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="no-print" style="text-align: center; margin-bottom: 20px;">
            <button onclick="window.print()">Imprimir</button>
            <button onclick="window.close()">Fechar</button>
          </div>
          ${logoHtml}
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSaveCriteria = async (newCriteria) => {
    try {
      setLoading(true);
      await championshipService.update(id, { 
        rankingCriteria: newCriteria 
      });
      await fetchData();
      setShowCriteriaModal(false);
    } catch (err) {
      console.error('Erro ao salvar critérios:', err);
      alert("Erro ao salvar critérios: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const championshipSupportsGroups = championship && hasGroups(championship.format);

  if (loadingAuth || (loading && !championship && !error)) {
    return <StandingsLoader />;
  }

  if (error && Object.keys(standings).length === 0 && !loading) {
      return <p className="error-message">{error}</p>;
  }
  
  if (!championship && !loading && !error) {
    return <p className="error-message">Não foi possível carregar os dados do campeonato.</p>;
  }

  return (
    <div className="standings-container printable-area">
      <div className="standings-header-controls">
        <h2 className="standings-title">Classificação</h2>
        
        <div className="header-buttons-group">
          {canManageGroups && championshipSupportsGroups && (
             <button 
               onClick={() => setShowCriteriaModal(true)}
               className="config-button action-button"
               style={{ backgroundColor: '#6c757d', marginRight: '10px' }}
             >
               ⚙️ Critérios
             </button>
          )}

          <button
              onClick={handlePrint}
              className="print-button action-button"
              disabled={Object.keys(standings).length === 0 || loading}
          >
              Exportar/Imprimir
          </button>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}
      
      {loading && <StandingsLoader />}

      {!loading && Object.keys(standings).length > 0 ? (
        <div className="standings-grid">
          {Object.entries(standings).map(([groupName, groupTeams]) => (
            <div key={groupName} className="group-standings-wrapper">
              <h3 className="group-title">{championship?.name} - {groupName}</h3>
              {Array.isArray(groupTeams) && groupTeams.length > 0 ? (
                <table className="standings-table">
                  <thead>
                    <tr>
                      <th>Pos</th>
                      <th className="team-header">Time</th>
                      <th>P</th>
                      <th>J</th>
                      <th>V</th>
                      <th>E</th>
                      <th>D</th>
                      <th>GP</th>
                      <th>GC</th>
                      <th>SG</th>
                      <th>CA</th>
                      <th>CV</th>
                      <th className="aproveitamento-header">AP%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupTeams.map((item, index) => {
                      const rankClass = index === 0 ? 'rank-1' : index === 1 ? 'rank-2' : '';
                      return (
                        <tr 
                          key={item.team._id} 
                          className={rankClass}
                          style={{ animationDelay: `${index * 0.08}s` }}
                        >
                          <td>{index === 0 ? '🥇' : index === 1 ? '🥈' : index + 1}</td>
                          <td className="team-cell">
                            <span>{item.team.name}</span>
                          </td>
                          <td>{item.points}</td>
                          <td>{item.matchesPlayed}</td>
                          <td>{item.wins}</td>
                          <td>{item.draws}</td>
                          <td>{item.losses}</td>
                          <td>{item.goalsFor}</td>
                          <td>{item.goalsAgainst}</td>
                          <td>{item.goalDifference}</td>
                          <td>{item.yellowCards || 0}</td>
                          <td>{item.redCards || 0}</td>
                          <td className="aproveitamento-cell">{calculateAproveitamento(item)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">Nenhum time neste grupo ou aguardando partidas.</p>
              )}
            </div>
          ))}
        </div>
      ) : !loading && !error && (
        <p className="no-data">
          {championshipSupportsGroups
            ? (canManageGroups ? "Ainda não há dados de classificação. Acesse 'Gerenciar Grupos' para começar." : "Ainda não há dados de classificação para esta fase.")
            : "Não há dados de classificação para este campeonato ou o formato não inclui grupos."}
        </p>
      )}

      <CriteriaModal 
        isOpen={showCriteriaModal}
        onClose={() => setShowCriteriaModal(false)}
        currentCriteria={championship?.rankingCriteria || ['points', 'wins', 'goalDifference', 'goalsFor', 'redCards', 'yellowCards', 'headToHead']}
        onSave={handleSaveCriteria}
      />
    </div>
  );
};

export default Standings;